import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link2, Calculator } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NomenclatureSimulateursDialog({ 
  nomenclature, 
  nomenclatures = [], // Pour les actions en masse
  isOpen, 
  onClose, 
  onUpdate 
}) {
  const { getAuthHeader } = useAuth();
  const [estimateurs, setSimulateurs] = useState([]);
  const [linkedSimulateurs, setLinkedSimulateurs] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [changes, setChanges] = useState(new Set());

  const isBulkMode = nomenclatures.length > 1;
  const nomenclatureIds = isBulkMode 
    ? nomenclatures.map(n => n.id)
    : nomenclature ? [nomenclature.id] : [];

  useEffect(() => {
    if (isOpen) {
      fetchSimulateurs();
      if (nomenclature) {
        fetchLinkedSimulateurs();
      }
    }
  }, [isOpen, nomenclature]);

  const fetchSimulateurs = async () => {
    try {
      const response = await axios.get(
        `${API}/estimateurs?active_only=true`,
        { headers: getAuthHeader() }
      );
      setSimulateurs(response.data);
    } catch {
      toast.error('Erreur lors du chargement des estimateurs');
    }
  };

  const fetchLinkedSimulateurs = async () => {
    if (!nomenclature) return;
    
    try {
      // Pour chaque estimateur, vérifier si cette nomenclature est liée
      const promises = estimateurs.map(est =>
        axios.get(
          `${API}/estimateurs/${est.id}/nomenclatures?linked_only=true`,
          { headers: getAuthHeader() }
        )
      );
      
      const responses = await Promise.all(promises);
      const linked = new Set();
      
      responses.forEach((response, index) => {
        const nomenclatures = response.data;
        if (nomenclatures.find(n => n.id === nomenclature.id)) {
          linked.add(estimateurs[index].id);
        }
      });
      
      setLinkedSimulateurs(linked);
    } catch {
    }
  };

  const toggleSimulateur = (estimateurId) => {
    setChanges(prev => {
      const newChanges = new Set(prev);
      if (newChanges.has(estimateurId)) {
        newChanges.delete(estimateurId);
      } else {
        newChanges.add(estimateurId);
      }
      return newChanges;
    });
  };

  const isLinked = (estimateurId) => {
    const wasLinked = linkedSimulateurs.has(estimateurId);
    const hasChange = changes.has(estimateurId);
    return hasChange ? !wasLinked : wasLinked;
  };

  const hasChanges = () => changes.size > 0;

  const handleSave = async () => {
    if (!hasChanges()) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      const promises = [];

      // Pour chaque changement
      for (const estimateurId of changes) {
        const wasLinked = linkedSimulateurs.has(estimateurId);
        
        if (wasLinked) {
          // Délier
          promises.push(
            axios.post(
              `${API}/estimateurs/${estimateurId}/nomenclatures/unlink`,
              {
                estimateur_id: estimateurId,
                nomenclature_ids: nomenclatureIds
              },
              { headers: getAuthHeader() }
            )
          );
        } else {
          // Lier
          promises.push(
            axios.post(
              `${API}/estimateurs/${estimateurId}/nomenclatures/link`,
              {
                estimateur_id: estimateurId,
                nomenclature_ids: nomenclatureIds
              },
              { headers: getAuthHeader() }
            )
          );
        }
      }

      await Promise.all(promises);
      
      const nomenclatureText = isBulkMode 
        ? `${nomenclatureIds.length} nomenclatures`
        : 'Nomenclature';
      
      toast.success(`${nomenclatureText} mise(s) à jour avec succès`);
      onUpdate();
      onClose();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            {isBulkMode 
              ? `Gérer les estimateurs (${nomenclatureIds.length} nomenclatures)`
              : `Gérer les estimateurs : ${nomenclature?.name}`
            }
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {estimateurs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Aucun estimateur actif</p>
              <p className="text-sm mt-2">Créez un estimateur pour commencer</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">
                {isBulkMode 
                  ? 'Sélectionnez les estimateurs à lier/délier pour les nomenclatures sélectionnées'
                  : 'Cochez pour lier, décochez pour délier'
                }
              </p>
              
              {estimateurs.map(estimateur => {
                const currentlyLinked = isLinked(estimateur.id);
                const hasChange = changes.has(estimateur.id);
                
                return (
                  <div
                    key={estimateur.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      hasChange 
                        ? 'border-blue-500 bg-blue-50' 
                        : currentlyLinked
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleSimulateur(estimateur.id)}
                  >
                    <input
                      type="checkbox"
                      checked={currentlyLinked}
                      onChange={() => toggleSimulateur(estimateur.id)}
                      className="rounded"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{estimateur.name}</span>
                        {estimateur.code && (
                          <Badge className="bg-slate-600 text-white text-xs font-semibold">
                            {estimateur.code}
                          </Badge>
                        )}
                      </div>
                      {estimateur.description && (
                        <p className="text-xs text-gray-500 mt-1">
                          {estimateur.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {currentlyLinked && !hasChange && (
                        <Badge variant="success" className="text-xs">
                          <Link2 className="h-3 w-3 mr-1" />
                          Lié
                        </Badge>
                      )}
                      {!currentlyLinked && !hasChange && (
                        <Badge className="bg-blue-600 text-white text-xs font-semibold">
                          Non lié
                        </Badge>
                      )}
                      {hasChange && (
                        <Badge variant="default" className="text-xs">
                          {currentlyLinked ? 'À lier' : 'À délier'}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="outline" disabled={loading}>
            Annuler
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading || !hasChanges()}
          >
            {loading ? 'Enregistrement...' : `Enregistrer${hasChanges() ? ` (${changes.size} changement${changes.size > 1 ? 's' : ''})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
